import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ClientDetailPage } from './pages/clients/[id]';
import { EditClientPage } from './pages/clients/edit';
import { ClientsListPage } from './pages/clients/index';
import { NewClientPage } from './pages/clients/new';
import { CrmLeadsListPage } from './pages/crm/leads/index';
import { NewCrmLeadPage } from './pages/crm/leads/new';
import { CrmLeadDetailPage } from './pages/crm/leads/[id]';
import { CrmDealsListPage } from './pages/crm/deals/index';
import { CrmDealsKanbanPage } from './pages/crm/deals/kanban';
import { NewCrmDealPage } from './pages/crm/deals/new';
import { CrmDealDetailPage } from './pages/crm/deals/[id]';
import { CrmProposalsListPage } from './pages/crm/proposals/index';
import { NewCrmProposalPage } from './pages/crm/proposals/new';
import { CrmProposalDetailPage } from './pages/crm/proposals/[id]';
import { MyTasksPage } from './pages/crm/activities/my-tasks';
import { CrmPipelinePage } from './pages/crm/pipeline';
import { ExternalPlansPage } from './pages/catalog/external-plans';
import { SaasProductsPage } from './pages/catalog/products';
import { PlanDetailPage } from './pages/plans/[id]';
import { PlansPage } from './pages/plans/index';
import { CouponsPage } from './pages/coupons/index';
import { AsaasAccountsPage } from './pages/asaas-accounts';
import { ChargeDetailPage } from './pages/charges/[id]';
import { ChargesPage } from './pages/charges';
import { NewChargePage } from './pages/charges/new';
import { FiscalHubPage } from './pages/fiscal/index';
import { PlansWizardPage } from './pages/plans/new/Wizard';
import { SubscriptionsPage } from './pages/subscriptions';
import { SubscriptionDetailPage } from './pages/subscriptions/[id]';
import { SubscriptionWizardPage } from './pages/subscriptions/new/Wizard';
import { DashboardPage } from './pages/dashboard';
import { AdminNotificationTemplatesPage } from './pages/admin/notification-templates';
import { AdminNotificationsDlqPage } from './pages/admin/notifications-dlq';
import { AdminSuspensionPolicyPage } from './pages/admin/suspension-policy';
import { DivergenceAlertsPage } from './pages/admin/divergence-alerts/index';
import { WebhookEventsPage } from './pages/webhook-events/index';
import { CommunicationsPage } from './pages/communications/index';
import { ReportsPage } from './pages/reports/index';
import { FinancialReportsPage } from './pages/reports/financial';
import { SettingsPage } from './pages/settings/index';
import {
  IntegrationOwlTaskPage,
  IntegrationReserveiPage,
  IntegrationVizzyPage,
} from './pages/integrations/saas-status';
import { ProvisioningQueuePage } from './pages/provisioning';
import { AwaitingReviewPage } from './pages/identities/awaiting-review';
import { ImportSaasDirectPage } from './pages/identities/import';
import { IntegrationsPage } from './pages/integrations/index';
import { ConnectSaasPage } from './pages/integrations/connect';
import { BootstrapRunDetailPage } from './pages/integrations/run-detail';
import { LoginPage } from './pages/login';
import { PublicCheckoutPage } from './pages/checkout/[slug]';
import { PlanChangePage } from './pages/checkout/trocar';
import { CheckoutPayPage } from './pages/checkout/pay';
import { ChargeCheckoutPage } from './pages/checkout/cobranca';
import { CheckoutSuccessPage } from './pages/checkout/success';
import { ReserveiCheckoutPreviewPage } from './pages/preview/reservei-checkout';
import { ReserveiCheckoutVisualPage } from './pages/preview/reservei-checkout-real';
import { PublicProposalPage } from './pages/public/proposal';
import { TermsOfUsePage } from './pages/legal/termos-de-uso';
import { PrivacyPolicyPage } from './pages/legal/politica-de-privacidade';
import { NotFoundPage } from './pages/placeholder';
import { WelcomePage } from './pages/welcome';
import { useUiStore } from './stores/useUiStore';

export function App(): JSX.Element {
  const theme = useUiStore((s) => s.theme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/pay/:chargeId" element={<CheckoutPayPage />} />
      <Route path="/checkout/cobranca/:chargeId" element={<ChargeCheckoutPage />} />
      <Route path="/checkout/trocar" element={<PlanChangePage />} />
      {/* Prévias do checkout do Reservei: só em desenvolvimento. Em produção um
          checkout de demonstração ("sem cobrança") não pode ficar aberto por link. */}
      {import.meta.env.DEV && (
        <Route path="/preview/reservei-checkout" element={<ReserveiCheckoutPreviewPage />} />
      )}
      {import.meta.env.DEV && (
        <Route path="/preview/checkout-reservei/:slug" element={<ReserveiCheckoutVisualPage />} />
      )}
      <Route path="/checkout/:slug" element={<PublicCheckoutPage />} />
      <Route path="/public/proposals/:token" element={<PublicProposalPage />} />
      <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<WelcomePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Workspace ============================================ */}
        <Route path="/identities" element={<ClientsListPage />} />
        <Route path="/identities/new" element={<NewClientPage />} />
        <Route path="/identities/:id" element={<ClientDetailPage />} />
        <Route path="/identities/:id/edit" element={<EditClientPage />} />
        <Route path="/crm/leads" element={<CrmLeadsListPage />} />
        <Route path="/crm/leads/new" element={<NewCrmLeadPage />} />
        <Route path="/crm/leads/:id" element={<CrmLeadDetailPage />} />
        <Route path="/crm/deals" element={<CrmDealsListPage />} />
        <Route path="/crm/deals/kanban" element={<CrmDealsKanbanPage />} />
        <Route path="/crm/deals/new" element={<NewCrmDealPage />} />
        <Route path="/crm/deals/:id" element={<CrmDealDetailPage />} />
        <Route path="/crm/proposals" element={<CrmProposalsListPage />} />
        <Route path="/crm/proposals/new" element={<NewCrmProposalPage />} />
        <Route path="/crm/proposals/:id" element={<CrmProposalDetailPage />} />
        <Route path="/crm/activities/my-tasks" element={<MyTasksPage />} />
        <Route path="/crm/pipeline" element={<CrmPipelinePage />} />
        <Route path="/catalog" element={<SaasProductsPage />} />
        <Route path="/catalog/products" element={<SaasProductsPage />} />
        <Route path="/catalog/plans" element={<ExternalPlansPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/coupons" element={<CouponsPage />} />
        <Route path="/plans/new" element={<PlansWizardPage />} />
        <Route path="/plans/:id" element={<PlanDetailPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions/new" element={<SubscriptionWizardPage />} />
        <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
        <Route path="/asaas-accounts" element={<AsaasAccountsPage />} />
        <Route path="/charges" element={<ChargesPage />} />
        <Route path="/charges/new" element={<NewChargePage />} />
        <Route path="/charges/:id" element={<ChargeDetailPage />} />
        <Route path="/fiscal" element={<FiscalHubPage />} />
        {/* Compat: URLs antigas → abas do hub (bookmarks/e-mails continuam válidos) */}
        <Route path="/fiscal/invoices" element={<Navigate to="/fiscal?tab=emitidas" replace />} />
        <Route
          path="/fiscal/recurring"
          element={<Navigate to="/fiscal?tab=recorrentes" replace />}
        />
        <Route path="/admin/notification-templates" element={<AdminNotificationTemplatesPage />} />
        <Route path="/admin/notifications/dlq" element={<AdminNotificationsDlqPage />} />
        <Route path="/admin/configuracoes/suspensao" element={<AdminSuspensionPolicyPage />} />
        <Route path="/admin/divergence-alerts" element={<DivergenceAlertsPage />} />
        <Route path="/webhook-events" element={<WebhookEventsPage />} />
        <Route path="/provisioning" element={<ProvisioningQueuePage />} />
        <Route path="/identities/awaiting-review" element={<AwaitingReviewPage />} />
        <Route path="/identities/import" element={<ImportSaasDirectPage />} />
        <Route path="/communications" element={<CommunicationsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/financial" element={<FinancialReportsPage />} />

        {/* Integrações ========================================== */}
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/integrations/connect/:slug" element={<ConnectSaasPage />} />
        <Route path="/integrations/runs/:runId" element={<BootstrapRunDetailPage />} />
        <Route path="/integrations/owltask" element={<IntegrationOwlTaskPage />} />
        <Route path="/integrations/reservei" element={<IntegrationReserveiPage />} />
        <Route path="/integrations/vizzy" element={<IntegrationVizzyPage />} />

        <Route path="/settings" element={<SettingsPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
